import { useEffect, useMemo, useState } from "react";
import { ApiError, fetchSettings, fetchTuningSuggestion, updateSettings } from "@/api/client";
import type { SecretStatusMap, SettingDef, SettingsMap, TuningSuggestionResponse } from "@/api/types";
import SecuritySettings from "@/components/SecuritySettings";
import NotificationSettings from "@/components/NotificationSettings";

interface SettingsPanelProps {
  open: boolean;
  onClose: () => void;
}

type SectionId = SettingDef["category"] | "security" | "notifications";
type FieldValue = number | boolean | string | string[] | null;

const SECTIONS: { id: SectionId; label: string; hint: string }[] = [
  { id: "trading", label: "Trading", hint: "Master switches, capital, leverage, scanning" },
  { id: "risk", label: "Risk Controls", hint: "Loss limits, entry gates, exits, exposure" },
  { id: "ai", label: "AI", hint: "Model, analysis inputs, decision threshold" },
  { id: "strategy", label: "Strategy", hint: "Timeframes, regime adapt, indicators, scalping" },
  { id: "backtest", label: "Backtest & Auto-Tune", hint: "AI tuning advisor" },
  { id: "meme", label: "Meme Sleeve (BSC)", hint: "Sleeve capital, exits, token screening" },
  { id: "notifications", label: "Notification Channels", hint: "Discord, Telegram, routing rules" },
  { id: "security", label: "Account Security", hint: "Password and two-factor" },
];

function humanizeOption(opt: string): string {
  const spaced = opt.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const WEEKDAY_OPTIONS: { code: string; label: string }[] = [
  { code: "SUN", label: "Sun" },
  { code: "MON", label: "Mon" },
  { code: "TUE", label: "Tue" },
  { code: "WED", label: "Wed" },
  { code: "THU", label: "Thu" },
  { code: "FRI", label: "Fri" },
  { code: "SAT", label: "Sat" },
];

function SecretControl({
  value,
  status,
  noun,
  onChange,
}: {
  value: FieldValue;
  status: SecretStatusMap[string] | undefined;
  noun: string;
  onChange: (value: FieldValue) => void;
}) {
  const removing = value === null;
  const typed = typeof value === "string" ? value : "";
  const capitalizedNoun = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;
  return (
    <div className="flex w-64 flex-col items-end gap-1.5">
      <input
        type="password"
        value={typed}
        disabled={removing}
        autoComplete="new-password"
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        data-lpignore="true"
        data-1p-ignore="true"
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          removing
            ? "Will be removed when you save"
            : status?.configured
              ? `Saved (${status.hint ?? "••••"}). Type to replace`
              : `Paste ${noun} here`
        }
        className="w-full rounded-md border border-line bg-raised px-2 py-1.5 font-mono text-xs text-ink outline-none placeholder:text-ink-faint focus:border-turquoise/60 disabled:opacity-60"
      />
      <div className="flex items-center gap-2 font-mono text-[10px] text-ink-faint">
        <span className="flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 rounded-full ${status?.configured && !removing ? "bg-profit" : "bg-ink-faint"}`} />
          {removing ? "Marked for removal" : status?.configured ? `${capitalizedNoun} stored, hidden` : `No ${noun} stored`}
        </span>
        {removing ? (
          <button type="button" onClick={() => onChange("")} className="text-turquoise hover:underline">
            Undo
          </button>
        ) : (
          status?.configured && (
            <button type="button" onClick={() => onChange(null)} className="text-sell hover:underline">
              Remove
            </button>
          )
        )}
      </div>
    </div>
  );
}

function FieldControl({
  def,
  value,
  secretStatus,
  onChange,
}: {
  def: SettingDef;
  value: FieldValue;
  secretStatus?: SecretStatusMap[string];
  onChange: (value: FieldValue) => void;
}) {
  if (def.secret) {
    return <SecretControl value={value} status={secretStatus} noun={def.secretNoun ?? "key"} onChange={onChange} />;
  }
  if (def.type === "json" && def.options) {
    const selected = Array.isArray(value) ? value : [];
    const isWeekdays = def.key === "allowedWeekdays";
    const keepCase = def.key === "scanTimeframes";
    const limitReached = def.maxItems !== undefined && selected.length >= def.maxItems;
    return (
      <div className="flex max-w-xs flex-wrap justify-end gap-1.5">
        {def.options.map((opt) => {
          const active = selected.includes(opt);
          const blocked = !active && limitReached;
          const label = isWeekdays
            ? (WEEKDAY_OPTIONS.find((w) => w.code === opt)?.label ?? opt)
            : humanizeOption(opt);
          return (
            <button
              key={opt}
              type="button"
              disabled={blocked}
              onClick={() =>
                onChange(active ? selected.filter((c) => c !== opt) : [...selected, opt])
              }
              className={`rounded-md border px-2 py-1 font-mono text-[11px] ${
                keepCase ? "normal-case" : "uppercase"
              } transition ${
                active
                  ? "border-turquoise/60 bg-turquoise/20 text-turquoise"
                  : blocked
                    ? "cursor-not-allowed border-line bg-raised text-ink-faint opacity-40"
                    : "border-line bg-raised text-ink-faint hover:border-line-soft"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  }
  if (def.type === "boolean") {
    return (
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative h-6 w-11 shrink-0 rounded-full border transition ${
          value ? "border-turquoise/60 bg-turquoise/30" : "border-line bg-raised"
        }`}
        aria-pressed={Boolean(value)}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-ink transition-all ${
            value ? "left-6" : "left-0.5"
          }`}
        />
      </button>
    );
  }
  if (def.type === "enum") {
    return (
      <select
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-line bg-raised px-2 py-1.5 font-mono text-xs text-ink outline-none focus:border-turquoise/60"
      >
        {def.options?.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
  if (def.type === "string") {
    return (
      <textarea
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="None"
        className="w-64 resize-y rounded-md border border-line bg-raised px-2 py-1.5 font-mono text-xs text-ink outline-none focus:border-turquoise/60"
      />
    );
  }
  return (
    <input
      type="number"
      value={String(value)}
      min={def.min}
      max={def.max}
      step="any"
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className="w-28 rounded-md border border-line bg-raised px-2 py-1.5 text-right font-mono text-xs text-ink outline-none focus:border-turquoise/60"
    />
  );
}

function resolveFieldValue(def: SettingDef, dirty: Record<string, FieldValue>, values: SettingsMap): FieldValue {
  if (def.secret) return def.key in dirty ? (dirty[def.key] ?? null) : "";
  if (def.key in dirty) return dirty[def.key] ?? def.default;
  if (def.key in values) return values[def.key] as number | boolean | string | string[];
  if (def.type === "json") {
    try {
      const parsed = JSON.parse(String(def.default));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return def.default;
}

function formatSuggestionValue(value: number | boolean | string): string {
  if (typeof value === "boolean") return value ? "On" : "Off";
  return String(value);
}

function formatAge(timestampSeconds: number): string {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000 - timestampSeconds));
  if (seconds < 90) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 90) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

function TuningAdvisorCard({
  suggestion,
  schemaByKey,
  values,
  dirty,
  onLoad,
  loadedCount,
}: {
  suggestion: TuningSuggestionResponse["suggestion"];
  schemaByKey: Map<string, SettingDef>;
  values: SettingsMap;
  dirty: Record<string, FieldValue>;
  onLoad: (changes: Record<string, FieldValue>) => void;
  loadedCount: number | null;
}) {
  if (!suggestion) {
    return (
      <div className="rounded-md border border-line-soft bg-raised/40 p-3 font-mono text-[11px] leading-snug text-ink-faint">
        No tuning alert yet. The advisor only speaks up when a backtest of your current settings comes back
        net-losing with enough trades to be meaningful.
      </div>
    );
  }
  const data = suggestion.event.data ?? {};
  const items = data.suggestions ?? [];
  const combined = data.combined;
  const pending = items.filter((item) => {
    const current = item.key in dirty ? dirty[item.key] : values[item.key];
    return current !== item.suggestedValue && schemaByKey.has(item.key);
  });
  return (
    <div className="space-y-3 rounded-md border border-gold/40 bg-gold/5 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-mono text-[11px] uppercase tracking-widest text-gold">Latest AI tuning check</div>
        <div className="font-mono text-[11px] text-ink-faint">
          {formatAge(suggestion.createdAt)}
          {data.trigger === "regime_adapt" ? " · after regime adapt" : data.trigger ? " · after settings save" : ""}
          {data.modelUsed ? ` · ${data.modelUsed}` : ""}
        </div>
      </div>
      {combined && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            ["Trades", String(combined.totalTrades)],
            ["Win rate", `${combined.winRatePercent}%`],
            ["Backtest PnL", `$${combined.totalPnlUsd}`],
            ["Max drawdown", `${combined.maxDrawdownPercent}%`],
          ].map(([label, value]) => (
            <div key={label} className="rounded-md border border-line bg-panel px-2 py-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">{label}</div>
              <div className={`font-mono text-xs ${label === "Backtest PnL" && combined.totalPnlUsd < 0 ? "text-sell" : "text-ink"}`}>
                {value}
              </div>
            </div>
          ))}
        </div>
      )}
      {data.diagnosis && <div className="font-mono text-xs leading-relaxed text-ink">{data.diagnosis}</div>}
      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((item) => {
            const def = schemaByKey.get(item.key);
            return (
              <div key={item.key} className="rounded-md border border-line bg-panel px-2.5 py-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2 font-mono text-xs text-ink">
                  <span>{def?.label ?? item.key}</span>
                  <span className="text-turquoise">
                    {formatSuggestionValue(item.currentValue)} → {formatSuggestionValue(item.suggestedValue)}
                  </span>
                </div>
                <div className="mt-0.5 font-mono text-[11px] leading-snug text-ink-faint">{item.reason}</div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-mono text-[11px] text-ink-faint">
          {data.applied
            ? "These changes were applied automatically."
            : loadedCount !== null
              ? `${loadedCount} change(s) loaded into the form. Review them, then press Save changes.`
              : "Nothing is changed until you load these into the form and save."}
        </div>
        {!data.applied && pending.length > 0 && (
          <button
            type="button"
            onClick={() =>
              onLoad(Object.fromEntries(pending.map((item) => [item.key, item.suggestedValue as FieldValue])))
            }
            className="rounded-md border border-gold/60 bg-gold/10 px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-gold transition hover:bg-gold/20"
          >
            Load {pending.length} suggestion{pending.length === 1 ? "" : "s"}
          </button>
        )}
      </div>
    </div>
  );
}

function SettingRow({
  def,
  value,
  modified,
  secretStatus,
  onChange,
}: {
  def: SettingDef;
  value: FieldValue;
  modified: boolean;
  secretStatus?: SecretStatusMap[string];
  onChange: (value: FieldValue) => void;
}) {
  return (
    <div
      className={`flex flex-col gap-2 border-l-2 pl-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${
        modified ? "border-gold" : "border-transparent"
      }`}
    >
      <div>
        <div className="font-mono text-xs text-ink">
          {def.label}
          {modified && <span className="ml-2 text-[10px] uppercase tracking-wide text-gold">modified</span>}
        </div>
        <div className="mt-0.5 font-mono text-[11px] leading-snug text-ink-faint">{def.description}</div>
      </div>
      <FieldControl def={def} value={value} secretStatus={secretStatus} onChange={onChange} />
    </div>
  );
}

function groupDefs(defs: SettingDef[]): Array<[string, SettingDef[]]> {
  const map = new Map<string, SettingDef[]>();
  for (const def of defs) {
    const name = def.group ?? "General";
    const list = map.get(name) ?? [];
    list.push(def);
    map.set(name, list);
  }
  return Array.from(map.entries());
}

export default function SettingsPanel({ open, onClose }: SettingsPanelProps) {
  const [section, setSection] = useState<SectionId>("trading");
  const [query, setQuery] = useState("");
  const [schema, setSchema] = useState<SettingDef[]>([]);
  const [values, setValues] = useState<SettingsMap>({});
  const [secrets, setSecrets] = useState<SecretStatusMap>({});
  const [dirty, setDirty] = useState<Record<string, FieldValue>>({});
  const [tuning, setTuning] = useState<TuningSuggestionResponse["suggestion"]>(null);
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [confirmDangerous, setConfirmDangerous] = useState(false);
  const [reauth, setReauth] = useState({ password: "", totpCode: "" });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setQuery("");
    setLoadedCount(null);
    fetchSettings()
      .then((res) => {
        setSchema(res.schema);
        setValues(res.settings);
        setSecrets(res.secrets ?? {});
        setDirty({});
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load settings"))
      .finally(() => setLoading(false));
    fetchTuningSuggestion()
      .then((res) => setTuning(res.suggestion))
      .catch(() => setTuning(null));
  }, [open]);

  const visibleSchema = useMemo(() => schema.filter((def) => def.key !== "enabledSymbols"), [schema]);
  const schemaByKey = useMemo(() => new Map(schema.map((def) => [def.key, def])), [schema]);

  const sections = useMemo(() => {
    const known = new Set<string>(SECTIONS.map((s) => s.id));
    const extra = Array.from(new Set(visibleSchema.map((d) => d.category)))
      .filter((category) => !known.has(category))
      .map((category) => ({
        id: category as SectionId,
        label: category.charAt(0).toUpperCase() + category.slice(1),
        hint: "",
      }));
    const isStandalone = (id: SectionId) => id === "security" || id === "notifications";
    const all = [...SECTIONS.filter((s) => !isStandalone(s.id)), ...extra, ...SECTIONS.filter((s) => isStandalone(s.id))];
    return loading || schema.length === 0
      ? all
      : all.filter((s) => isStandalone(s.id) || visibleSchema.some((d) => d.category === s.id));
  }, [visibleSchema, loading, schema.length]);

  const dirtyBySection = useMemo(() => {
    const counts = new Map<string, number>();
    for (const key of Object.keys(dirty)) {
      const category = schemaByKey.get(key)?.category;
      if (category) counts.set(category, (counts.get(category) ?? 0) + 1);
    }
    return counts;
  }, [dirty, schemaByKey]);

  const searching = query.trim().length > 0;

  const shownCategories = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = (def: SettingDef) =>
      needle.length === 0 ||
      def.label.toLowerCase().includes(needle) ||
      def.key.toLowerCase().includes(needle) ||
      def.description.toLowerCase().includes(needle) ||
      (def.group ?? "").toLowerCase().includes(needle);
    const categories =
      needle.length > 0 ? sections.map((s) => s.id).filter((id) => id !== "security" && id !== "notifications") : [section];
    return categories
      .map((category) => ({
        category,
        label: sections.find((s) => s.id === category)?.label ?? category,
        groups: groupDefs(visibleSchema.filter((def) => def.category === category && matches(def))),
      }))
      .filter((entry) => entry.groups.length > 0);
  }, [query, section, sections, visibleSchema]);

  const hasDangerousChange =
    ("emergencyStop" in dirty && dirty.emergencyStop === true) ||
    ("tradingEnabled" in dirty && dirty.tradingEnabled === false);

  const needsReauth = Object.keys(dirty).some((key) => schemaByKey.get(key)?.sensitive === true);
  const dirtyCount = Object.keys(dirty).length;
  const onSecurity = section === "security" && !searching;
  const onNotifications = section === "notifications" && !searching;
  const onStandalone = onSecurity || onNotifications;

  function handleLoadSuggestions(changes: Record<string, FieldValue>) {
    setDirty((prev) => ({ ...prev, ...changes }));
    setLoadedCount(Object.keys(changes).length);
    setSavedAt(null);
  }

  async function handleSave() {
    if (dirtyCount === 0) return;
    if (hasDangerousChange && !confirmDangerous) {
      setConfirmDangerous(true);
      return;
    }
    if (needsReauth && reauth.password === "") {
      setError("Enter your password to confirm the profit-sweep changes");
      return;
    }
    const totpCode = reauth.totpCode.trim();
    const confirmation = needsReauth
      ? totpCode === ""
        ? { password: reauth.password }
        : { password: reauth.password, totpCode }
      : undefined;
    setSaving(true);
    setError(null);
    try {
      const res = await updateSettings(dirty, confirmation);
      setValues(res.settings);
      setSecrets(res.secrets ?? {});
      setDirty({});
      setReauth({ password: "", totpCode: "" });
      setConfirmDangerous(false);
      setLoadedCount(null);
      setSavedAt(Date.now());
    } catch (err) {
      if (err instanceof ApiError && err.details.length > 0) {
        setError(
          err.details
            .map((item) => `${schemaByKey.get(item.field)?.label ?? item.field}: ${item.message}`)
            .join(" | "),
        );
      } else {
        setError(err instanceof Error ? err.message : "Failed to save settings");
      }
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-void/80 px-4 py-8 backdrop-blur-sm">
      <div className="w-full max-w-4xl rounded-lg border border-line bg-panel shadow-panel">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div className="font-mono text-sm font-semibold uppercase tracking-widest text-ink">Settings</div>
          <button
            onClick={onClose}
            className="rounded-md border border-line px-2.5 py-1 font-mono text-xs text-ink-dim transition hover:border-sell/50 hover:text-sell"
          >
            Close
          </button>
        </div>

        <div className="flex flex-col sm:flex-row">
          <nav className="flex gap-1 overflow-x-auto border-b border-line p-2 sm:w-56 sm:shrink-0 sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-r sm:p-3">
            {sections.map((s) => {
              const active = s.id === section && !searching;
              const count = dirtyBySection.get(s.id) ?? 0;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setSection(s.id);
                    setQuery("");
                  }}
                  className={`shrink-0 rounded-md border px-3 py-2 text-left transition ${
                    active
                      ? "border-turquoise/50 bg-turquoise/10"
                      : "border-transparent hover:border-line hover:bg-raised/60"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`font-mono text-xs font-semibold uppercase tracking-wide ${
                        active ? "text-turquoise" : "text-ink-dim"
                      }`}
                    >
                      {s.label}
                    </span>
                    {count > 0 && (
                      <span className="rounded-full bg-gold/20 px-1.5 font-mono text-[10px] text-gold">{count}</span>
                    )}
                  </div>
                  <div className="mt-0.5 hidden font-mono text-[10px] leading-snug text-ink-faint sm:block">{s.hint}</div>
                </button>
              );
            })}
          </nav>

          <div className="min-w-0 flex-1">
            {!onStandalone && (
              <div className="border-b border-line px-5 py-3">
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search all settings…"
                  className="w-full rounded-md border border-line bg-raised px-3 py-1.5 font-mono text-xs text-ink outline-none placeholder:text-ink-faint focus:border-turquoise/60"
                />
              </div>
            )}

            {onStandalone ? (
              <div className="max-h-[70vh] overflow-y-auto px-5 py-5">
                {onSecurity ? <SecuritySettings /> : <NotificationSettings />}
              </div>
            ) : (
              <>
                <div className="max-h-[62vh] space-y-6 overflow-y-auto px-5 py-5">
                  {loading && <div className="font-mono text-xs text-ink-faint">Loading settings…</div>}
                  {error && (
                    <div className="rounded-md border border-sell/40 bg-sell/10 px-3 py-2 font-mono text-xs text-sell">
                      {error}
                    </div>
                  )}
                  {!loading && section === "backtest" && !searching && (
                    <TuningAdvisorCard
                      suggestion={tuning}
                      schemaByKey={schemaByKey}
                      values={values}
                      dirty={dirty}
                      onLoad={handleLoadSuggestions}
                      loadedCount={loadedCount}
                    />
                  )}
                  {!loading && shownCategories.length === 0 && searching && (
                    <div className="font-mono text-xs text-ink-faint">No settings match “{query.trim()}”.</div>
                  )}
                  {!loading &&
                    shownCategories.map(({ category, label, groups }) => (
                      <div key={category} className="space-y-4">
                        {searching && (
                          <div className="font-mono text-[11px] uppercase tracking-widest text-turquoise">{label}</div>
                        )}
                        {groups.map(([groupName, defs]) => (
                          <div key={groupName}>
                            <div className="mb-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-ink-dim">
                              <span>{groupName}</span>
                              <span className="text-ink-faint">{defs.length}</span>
                            </div>
                            <div className="space-y-4 rounded-md border border-line-soft bg-raised/40 p-3">
                              {defs.map((def) => (
                                <SettingRow
                                  key={def.key}
                                  def={def}
                                  value={resolveFieldValue(def, dirty, values)}
                                  modified={def.key in dirty}
                                  secretStatus={secrets[def.key]}
                                  onChange={(v) => {
                                    setDirty((prev) => {
                                      if (def.secret && v === "") {
                                        const next = { ...prev };
                                        delete next[def.key];
                                        return next;
                                      }
                                      return { ...prev, [def.key]: v };
                                    });
                                    setSavedAt(null);
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                </div>

                {needsReauth && (
                  <div className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-3">
                    <span className="font-mono text-[11px] text-ink-dim">Profit-sweep changes need your password:</span>
                    <input
                      type="password"
                      value={reauth.password}
                      autoComplete="current-password"
                      placeholder="Password"
                      onChange={(e) => setReauth((prev) => ({ ...prev, password: e.target.value }))}
                      className="w-40 rounded-md border border-line bg-raised px-2 py-1.5 font-mono text-xs text-ink outline-none placeholder:text-ink-faint focus:border-turquoise/60"
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={reauth.totpCode}
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="2FA code (if on)"
                      onChange={(e) => setReauth((prev) => ({ ...prev, totpCode: e.target.value }))}
                      className="w-36 rounded-md border border-line bg-raised px-2 py-1.5 font-mono text-xs text-ink outline-none placeholder:text-ink-faint focus:border-turquoise/60"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-line px-5 py-4">
                  <div className="font-mono text-[11px] text-ink-faint">
                    {confirmDangerous
                      ? "This halts new entries immediately. Save again to confirm."
                      : savedAt
                        ? "Saved."
                        : dirtyCount > 0
                          ? `${dirtyCount} unsaved change(s) across all sections`
                          : "No changes"}
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving || dirtyCount === 0}
                    className={`rounded-md border px-4 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider transition ${
                      confirmDangerous
                        ? "border-sell/60 bg-sell/20 text-sell hover:bg-sell/30"
                        : "border-turquoise/50 bg-turquoise/10 text-turquoise hover:bg-turquoise/20"
                    } disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    {saving ? "Saving…" : confirmDangerous ? "Confirm" : "Save changes"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
